'use client';

import { c as _c } from "react/compiler-runtime";
import { isDefined } from '@morpho-org/morpho-ts';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { LoaderGradient } from '@repo/ui/icons';
import { type ForwardedRef, forwardRef, memo, type ReactElement, type ReactNode, useState } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
import { Wrapper } from '../Wrapper/Wrapper';
interface ActionButtonsProps {
  isLoading: boolean;
  loadingLabel?: string;
  handleFinalize?: () => void;
  finalizeLabel: ReactNode;
  isFinalizeDisabled: boolean;
  /**
   * An amount input has not committed its value yet: the button stays visually
   * settled (no grey flash per keystroke) and clicks are swallowed as if disabled.
   */
  isInputPending?: boolean;
  /**
   * The amount is committed but the flow's checks are still debounced: holds the
   * last settled look only after a typing burst (instant changes like MAX or
   * clearing the box apply immediately); clicks follow `isFinalizeDisabled`.
   */
  isValidating?: boolean;
  tooltipContent?: ReactNode;
  popoverComponent?: ReactElement;
}

// While an amount is still settling, the button keeps the last settled
// disabled look instead of tracking `isFinalizeDisabled` on every keystroke.
// Validation only holds the look after a typing burst (`typedBurst`): instant
// changes that skip the input debounce (MAX) apply right away, and a committed
// disabled value (clearing the box) ends the hold — only a committed enabled
// value waits for the checks to settle.
function useSettledLook(isFinalizeDisabled, isInputPending, isValidating) {
  const $ = _c(3);
  const [typedBurst, setTypedBurst] = useState(isInputPending);
  if (isInputPending && !typedBurst) {
    setTypedBurst(true);
  } else {
    if (!isInputPending && (!isValidating || isFinalizeDisabled) && typedBurst) {
      setTypedBurst(false);
    }
  }
  const holding = isInputPending || isValidating && typedBurst;
  const [settledDisabled, setSettledDisabled] = useState(isFinalizeDisabled);
  if (!holding && settledDisabled !== isFinalizeDisabled) {
    setSettledDisabled(isFinalizeDisabled);
  }
  const t0 = holding ? settledDisabled : isFinalizeDisabled;
  let t1;
  if ($[0] !== holding || $[1] !== t0) {
    t1 = {
      holding,
      lookDisabled: t0
    };
    $[0] = holding;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * ActionButtons component handles the final action buttons for various action panels and dialogs.
 *
 * This component displays different buttons based on various states:
 * - Shows "Connect" when wallet is not connected
 * - Shows a loading state when isLoading is true
 * - Shows finalize and confirm buttons when ready for action
 *
 * @param props - {@link ActionButtonsProps}
 * @returns A stack of conditional buttons based on the current state
 */
const ActionButtons = forwardRef((t0, ref) => {
  const $ = _c(15);
  const {
    isLoading,
    loadingLabel,
    handleFinalize,
    finalizeLabel,
    tooltipContent,
    isFinalizeDisabled,
    isInputPending: t1,
    isValidating: t2,
    popoverComponent
  } = t0;
  const isInputPending = t1 === undefined ? false : t1;
  const isValidating = t2 === undefined ? false : t2;
  const {
    account,
    modal,
    switchToTargetChain
  } = useWeb3Context();
  const isWrongChain = account.isConnected && account.isWrongChain;
  const {
    holding,
    lookDisabled
  } = useSettledLook(isFinalizeDisabled, isInputPending, isValidating);
  const blocked = isFinalizeDisabled || isInputPending;
  let t3;
  if ($[0] !== account.isConnected || $[1] !== blocked || $[2] !== finalizeLabel || $[3] !== handleFinalize || $[4] !== holding || $[5] !== isLoading || $[6] !== isWrongChain || $[7] !== loadingLabel || $[8] !== lookDisabled || $[9] !== modal || $[10] !== popoverComponent || $[11] !== ref || $[12] !== switchToTargetChain || $[13] !== tooltipContent) {
    t3 = <>{!account.isConnected ? <div className="gap-xs pb-sm flex flex-col items-stretch justify-start max-md:order-[-1] max-md:pb-0"><Button size="lg" onClick={() => modal.open()}>Connect</Button></div> : <div className="gap-xs flex flex-col items-stretch justify-start" ref={ref}>{isLoading ? <Button variant="secondary" size="lg" disabled={true} data-testid="finalize-button-loading" className="disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"><LoaderGradient />{loadingLabel || "Loading onchain state"}</Button> : <Wrapper component={popoverComponent}><Tooltip><TooltipTrigger asChild={true}><Button size="lg" autoCapitalize="on" variant={isWrongChain ? undefined : lookDisabled ? "secondary" : undefined} disabled={!isWrongChain && !holding && lookDisabled} aria-disabled={!isWrongChain && blocked ? true : undefined} onClick={isWrongChain ? switchToTargetChain : () => {
                if (!blocked) {
                  handleFinalize?.();
                }
              }} data-testid="finalize-button" className={cn("transition-none disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100", "aria-disabled:opacity-100 aria-disabled:cursor-pointer", lookDisabled && !isWrongChain && "bg-muted text-muted-foreground border-transparent shadow-none hover:bg-muted active:bg-muted aria-disabled:bg-muted aria-disabled:text-muted-foreground aria-disabled:border-transparent aria-disabled:shadow-none")}><span className="block max-w-[220px] truncate">{isWrongChain ? "Switch chain" : finalizeLabel}</span></Button></TooltipTrigger>{!isWrongChain && isDefined(tooltipContent) && <TooltipContent>{tooltipContent}</TooltipContent>}</Tooltip></Wrapper>}</div>}</>;
    $[0] = account.isConnected;
    $[1] = blocked;
    $[2] = finalizeLabel;
    $[3] = handleFinalize;
    $[4] = holding;
    $[5] = isLoading;
    $[6] = isWrongChain;
    $[7] = loadingLabel;
    $[8] = lookDisabled;
    $[9] = modal;
    $[10] = popoverComponent;
    $[11] = ref;
    $[12] = switchToTargetChain;
    $[13] = tooltipContent;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
});
ActionButtons.displayName = 'ActionButtons';
export default memo(ActionButtons);